import { Dialog } from "./dialog";
import { cn } from "@hey404/ui";
import {
  inputClass,
  btnAccent,
  btnPrimary,
  btnSecondary,
  fieldErrorClass,
  labelClass,
} from "./admin-ui";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Bell,
  Bookmark,
  BookmarkCheck,
  CalendarDays,
  Check,
  ChevronLeft,
  Code2,
  Copy,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Dumbbell,
  Map,
  NotebookPen,
  Send,
  Sparkles,
  Trash2,
  Users,
  Video,
} from "lucide-react";
import { apiFetch } from "./lib/auth";
import { isAnswered, QuestionInput, useQuizAnswers } from "./quiz-runner";
import type { PublicQuestion } from "@hey404/quiz";
import { Markdown } from "./markdown";
import { CompanionSkeleton, PanelSkeleton, TabPanelSkeleton } from "./loading";
import { useQuery, useQueryClient } from "@tanstack/react-query";

type LessonDetails = {
  lesson: { id: string; transcript: string; kind: string };
  resources: Array<{
    id: string;
    title: string;
    url: string | null;
    content: string | null;
    kind: string;
    downloadable: boolean;
  }>;
  notes: Array<{
    id: string;
    body: string;
    positionSeconds: number;
    createdAt: string;
  }>;
  bookmarked: boolean;
  progress?: { positionSeconds: number };
  quiz: { id: string; passingScore: number } | null;
  project: { id: string; instructions: string; rubric: string } | null;
};

// A note's date label (es-AR) — notes are stamped by when they were written,
// not by a position in the video.
// The video minute a note was taken at, as 02:08 (or 1:02:08 past an hour).
const noteMinute = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h
    ? `${h}:${String(m).padStart(2, "0")}:${s}`
    : `${String(m).padStart(2, "0")}:${s}`;
};

// A single lesson resource. Code resources render their pasted snippet inline
// with a copy button; everything else is a link (downloadable or external).
function ResourceItem({
  resource,
}: {
  resource: LessonDetails["resources"][number];
}) {
  const [copied, setCopied] = useState(false);
  if (resource.kind === "code" && resource.content) {
    return (
      <div className="border-ink bg-bone border-chip rounded-control overflow-hidden">
        <div className="border-ink border-b-(length:--border-chip) flex items-center gap-2 px-3 py-2">
          <Code2 size={15} />
          <span className="font-display flex-1 font-bold">
            {resource.title}
          </span>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(resource.content ?? "");
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className={`${copied ? btnAccent : btnSecondary} gap-1.5 px-3 py-1.5 text-xs`}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
        <pre className="overflow-x-auto p-3 font-mono text-[12px] leading-relaxed">
          <code>{resource.content}</code>
        </pre>
      </div>
    );
  }
  return (
    <a
      href={resource.url ?? "#"}
      target="_blank"
      rel="noreferrer"
      download={resource.downloadable || undefined}
      className="border-ink bg-bone border-chip rounded-control hover:bg-paper group flex items-center gap-3 p-3 transition-colors"
    >
      <span
        aria-hidden="true"
        className="border-ink bg-paper border-chip grid h-9 w-9 shrink-0 place-items-center"
      >
        {resource.downloadable ? (
          <Download size={16} />
        ) : (
          <ExternalLink size={16} />
        )}
      </span>
      <span className="font-display flex-1 font-bold group-hover:underline">
        {resource.title}
      </span>
      <span className="text-ink-soft font-mono text-xs">
        {resource.downloadable ? "Descargar" : "Abrir"}
      </span>
    </a>
  );
}

/**
 * "Guardar clase", next to the lesson title. Saving opens a confirmation
 * (where it was saved) with an optional comment, stored as a note on the
 * lesson (UNB2-160). Shares the lesson query with LessonCompanion.
 */
export function LessonBookmark({
  lessonId,
  positionSeconds,
}: {
  lessonId: string;
  positionSeconds: number;
}) {
  const queryClient = useQueryClient();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveComment, setSaveComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const detailsQuery = useQuery({
    queryKey: ["student", "lesson", lessonId],
    queryFn: () =>
      apiFetch<LessonDetails>(`/api/education/lessons/${lessonId}`),
  });
  const load = () =>
    queryClient.invalidateQueries({
      queryKey: ["student", "lesson", lessonId],
    });
  // Close the "clase guardada" modal, first persisting the comment as a note if
  // the student wrote one. A single "Listo" (or dismissing the modal) does both.
  const finishSave = async () => {
    const comment = saveComment.trim();
    if (comment) {
      setSavingComment(true);
      try {
        await apiFetch(`/api/education/lessons/${lessonId}/notes`, {
          method: "POST",
          body: JSON.stringify({
            body: comment,
            positionSeconds: Math.floor(positionSeconds),
          }),
        });
        await load();
      } finally {
        setSavingComment(false);
      }
    }
    setSaveComment("");
    setSaveModalOpen(false);
  };
  if (!detailsQuery.data) return null;
  const bookmarked = detailsQuery.data.bookmarked;
  return (
    <>
      <button
        onClick={async () => {
          const next = !bookmarked;
          await apiFetch(`/api/education/lessons/${lessonId}/bookmark`, {
            method: "PUT",
            body: JSON.stringify({ bookmarked: next }),
          });
          queryClient.setQueryData<LessonDetails>(
            ["student", "lesson", lessonId],
            (current) => current && { ...current, bookmarked: next },
          );
          // Only on save (not un-save): confirm where it went + offer a comment.
          if (next) {
            setSaveComment("");
            setSaveModalOpen(true);
          }
        }}
        // Same button system as the rest of the lesson actions; saved
        // lessons read as done (green), unsaved ones as secondary.
        aria-pressed={bookmarked}
        aria-label={bookmarked ? "Clase guardada" : "Guardar clase"}
        title={bookmarked ? "Clase guardada" : "Guardar clase"}
        className={`${bookmarked ? btnAccent : btnSecondary} min-h-10 shrink-0 gap-2 px-4 py-2 text-sm`}
      >
        {bookmarked ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
        <span>{bookmarked ? "Guardada" : "Guardar clase"}</span>
      </button>
      <Dialog
        open={saveModalOpen}
        onClose={finishSave}
        title="Clase guardada"
        icon={BookmarkCheck}
        tone="acid"
        description={
          <>
            La encuentras cuando quieras en <strong>Guardadas</strong>.
          </>
        }
        footer={
          <button
            type="button"
            disabled={savingComment}
            onClick={finishSave}
            className={`${btnAccent} px-4 py-2.5 text-sm`}
          >
            {savingComment ? "Guardando…" : "Listo"}
          </button>
        }
      >
        <label className="block">
          <span className={labelClass}>
            ¿Quieres dejar una nota?{" "}
            <span className="text-ink-soft font-normal">(opcional)</span>
          </span>
          <textarea
            value={saveComment}
            onChange={(e) => setSaveComment(e.target.value)}
            rows={3}
            placeholder="Una nota para tu yo del futuro"
            className={cn(inputClass, "mt-1.5 px-3 py-2.5 text-base")}
          />
        </label>
        <p className="text-ink-soft mt-1.5 text-xs">
          Queda en las <strong>Notas</strong> de esta clase.
        </p>
      </Dialog>
    </>
  );
}

export function LessonCompanion({
  lessonId,
  positionSeconds,
  onResume,
}: {
  lessonId: string;
  positionSeconds: number;
  onResume?: (positionSeconds: number) => void;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<
    "resources" | "notes" | "transcript" | "practice"
  >("resources");
  const [openNote, setOpenNote] = useState<
    LessonDetails["notes"][number] | null
  >(null);
  const detailsQuery = useQuery({
    queryKey: ["student", "lesson", lessonId],
    queryFn: () =>
      apiFetch<LessonDetails>(`/api/education/lessons/${lessonId}`),
  });
  const details = detailsQuery.data;
  const load = () =>
    queryClient.invalidateQueries({
      queryKey: ["student", "lesson", lessonId],
    });
  const deleteNote = async (id: string) => {
    await apiFetch(`/api/education/notes/${id}`, { method: "DELETE" });
    await load();
  };
  useEffect(() => {
    if (details) onResume?.(details.progress?.positionSeconds ?? 0);
  }, [details, onResume]);
  if (detailsQuery.isError)
    return (
      <section className="border-ink bg-bubble border-surface mt-7 p-5">
        <strong className="font-display">No pudimos cargar esta clase.</strong>
        <p className="text-ink-soft mt-1 text-sm">
          Revisa tu conexión o acceso y vuelve a intentarlo.
        </p>
      </section>
    );
  if (!details) return <CompanionSkeleton />;
  const tabs = [
    ["resources", "Recursos", FolderOpen, details.resources.length],
    ["notes", "Notas", NotebookPen, details.notes.length],
    ["transcript", "Transcripción", FileText, null],
    ["practice", "Práctica", Dumbbell, null],
  ] as const;
  return (
    <section className="border-ink bg-paper border-surface @container mt-7">
      <div className="border-ink/20 flex items-center justify-between gap-3 border-b border-dashed p-3 sm:p-4">
        <div
          role="tablist"
          aria-label="Contenido de la clase"
          className="border-ink bg-bone border-chip rounded-control flex max-w-full gap-1 overflow-x-auto p-1"
        >
          {tabs.map(([id, label, Icon, count], index) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`panel-${id}`}
              tabIndex={tab === id ? 0 : -1}
              onClick={() => setTab(id)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowRight" && event.key !== "ArrowLeft")
                  return;
                event.preventDefault();
                const next =
                  event.key === "ArrowRight"
                    ? (index + 1) % tabs.length
                    : (index - 1 + tabs.length) % tabs.length;
                const nextTab = tabs[next];
                if (!nextTab) return;
                const nextId = nextTab[0];
                setTab(nextId);
                document.getElementById(`tab-${nextId}`)?.focus();
              }}
              // V30 navigation: regular text, the accent marks the active tab.
              className={cn(
                "flex min-h-10 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[6px] px-3 text-sm transition-colors",
                tab === id ? "bg-ink text-paper" : "hover:bg-paper-dim",
              )}
            >
              <Icon size={15} aria-hidden="true" />
              {label}
              {count !== null && count > 0 && (
                <span
                  className={cn(
                    "min-w-5 rounded-full px-1.5 py-0.5 text-center font-mono text-[11px] font-bold leading-none",
                    tab === id ? "bg-lemon text-ink" : "bg-ink/10",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div
        role="tabpanel"
        id={`panel-${tab}`}
        aria-labelledby={`tab-${tab}`}
        tabIndex={0}
        className="p-5"
      >
        {tab === "resources" && (
          <div className="space-y-2">
            {details.resources.length ? (
              details.resources.map((resource) => (
                <ResourceItem key={resource.id} resource={resource} />
              ))
            ) : (
              <p className="text-ink-soft flex items-center gap-2 text-sm">
                <FolderOpen size={16} aria-hidden="true" />
                Esta clase no tiene recursos adicionales.
              </p>
            )}
          </div>
        )}
        {tab === "notes" && (
          <div>
            <form
              className="grid gap-1.5"
              onSubmit={async (event) => {
                event.preventDefault();
                const form = event.currentTarget;
                const data = new FormData(form);
                await apiFetch(`/api/education/lessons/${lessonId}/notes`, {
                  method: "POST",
                  body: JSON.stringify({
                    body: data.get("body"),
                    positionSeconds: Math.floor(positionSeconds),
                  }),
                });
                form.reset();
                await load();
              }}
            >
              <label htmlFor="lesson-note" className={labelClass}>
                Nueva nota
              </label>
              <div className="flex gap-2">
                <input
                  id="lesson-note"
                  name="body"
                  required
                  placeholder="Lo que quieres recordar de esta clase"
                  className={cn(
                    inputClass,
                    "mt-0 min-w-0 flex-1 px-3 py-2.5 text-base",
                  )}
                />
                <button className={`${btnAccent} shrink-0 gap-2 px-4 text-sm`}>
                  <NotebookPen size={16} aria-hidden="true" /> Guardar
                </button>
              </div>
              <p className="text-ink-soft text-xs">
                Se guarda con el minuto del video en el que vas.
              </p>
            </form>
            <div className="mt-4 space-y-2">
              {details.notes.map((note) => (
                // Every note keeps one line (long text ends in "…"); the row
                // opens the full note in a dialog.
                <div
                  key={note.id}
                  className="border-ink bg-bone border-chip rounded-control hover:bg-paper flex items-center gap-2 pr-1.5 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setOpenNote(note)}
                    aria-label={`Ver nota del minuto ${noteMinute(note.positionSeconds)}`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 py-2.5 pl-3 text-left"
                  >
                    <span className="border-ink bg-lemon border-chip shrink-0 px-1.5 py-0.5 font-mono text-xs font-bold tabular-nums">
                      {noteMinute(note.positionSeconds)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {note.body}
                    </span>
                  </button>
                  <button
                    aria-label="Eliminar nota"
                    title="Eliminar nota"
                    className="text-ink-soft hover:bg-ink hover:text-paper rounded-control grid h-8 w-8 shrink-0 cursor-pointer place-items-center transition-colors"
                    onClick={() => deleteNote(note.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <Dialog
              open={openNote !== null}
              onClose={() => setOpenNote(null)}
              kicker="Tu nota"
              title={
                openNote
                  ? `Minuto ${noteMinute(openNote.positionSeconds)}`
                  : "Nota"
              }
              icon={NotebookPen}
              tone="lemon"
              description={
                openNote
                  ? `Guardada el ${new Date(openNote.createdAt).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })}.`
                  : undefined
              }
              footer={
                <>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!openNote) return;
                      const id = openNote.id;
                      setOpenNote(null);
                      await deleteNote(id);
                    }}
                    className={`${btnSecondary} hover:bg-ink hover:text-paper mr-auto gap-1.5 px-4 py-2.5 text-sm`}
                  >
                    <Trash2 size={14} aria-hidden="true" /> Eliminar
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenNote(null)}
                    className={`${btnAccent} px-4 py-2.5 text-sm`}
                  >
                    Listo
                  </button>
                </>
              }
            >
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed">
                {openNote?.body}
              </p>
            </Dialog>
          </div>
        )}
        {tab === "transcript" && (
          <Markdown className="max-h-96 overflow-y-auto text-sm leading-relaxed">
            {details.lesson.transcript ||
              "_La transcripción todavía no está disponible._"}
          </Markdown>
        )}
        {tab === "practice" && (
          <div>
            {details.quiz ? <QuizPanel quizId={details.quiz.id} /> : null}
            {details.project ? (
              <ProjectPanel projectId={details.project.id} />
            ) : null}
            {!details.quiz && !details.project && (
              <p className="text-ink-soft text-sm">
                Esta clase no tiene una actividad práctica.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function QuizPanel({ quizId }: { quizId: string }) {
  type QuizData = {
    quiz: { passingScore: number };
    questions: PublicQuestion[];
  };
  const quizQuery = useQuery({
    queryKey: ["student", "quiz", quizId],
    queryFn: () => apiFetch<QuizData>(`/api/education/quizzes/${quizId}`),
  });
  const data = quizQuery.data;
  const { answers, set, reset } = useQuizAnswers();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{
    score: number;
    passed: boolean;
    pendingReview: number;
  } | null>(null);
  if (quizQuery.isError)
    return <p className={fieldErrorClass}>No pudimos cargar el quiz.</p>;
  if (!data) return <TabPanelSkeleton rows={4} />;
  const total = data.questions.length;
  const answered = data.questions.filter((question) =>
    isAnswered(question, answers[question.id]),
  ).length;
  const complete = answered === total;
  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (!complete || sending) return;
        setSending(true);
        setError("");
        try {
          const response = await apiFetch<{
            attempt: { score: number; passed: boolean };
            pendingReview: number;
          }>(`/api/education/quizzes/${quizId}/attempts`, {
            method: "POST",
            body: JSON.stringify({ answers }),
          });
          setResult({
            ...response.attempt,
            pendingReview: response.pendingReview,
          });
        } catch {
          setError(
            "No pudimos revisar tus respuestas. Si ya usaste todos tus intentos, habla con el equipo.",
          );
        } finally {
          setSending(false);
        }
      }}
    >
      {/* What this is and how far along you are. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.06em]">
            Quiz de la clase
          </p>
          <p className="mt-1 text-[15px]">
            {total} {total === 1 ? "pregunta" : "preguntas"} · apruebas con{" "}
            <strong>{data.quiz.passingScore}%</strong>
          </p>
        </div>
        <p className="text-ink-soft font-mono text-xs">
          {answered} de {total} respondidas
        </p>
      </div>
      <div
        role="progressbar"
        aria-label="Preguntas respondidas"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
        className="bg-ink/10 mt-2 h-1.5 overflow-hidden rounded-full"
      >
        <div
          className="bg-acid h-full transition-[width] duration-300"
          style={{ width: `${total ? (answered / total) * 100 : 0}%` }}
        />
      </div>

      <ol className="mt-5 space-y-4">
        {data.questions.map((question, index) => {
          const done = isAnswered(question, answers[question.id]);
          return (
            <li key={question.id}>
              <fieldset className="border-ink bg-bone border-chip rounded-control m-0 min-w-0 p-4 sm:p-5">
                {/* Legend as a normal block: the question sits above its
                    answers instead of cutting through the card's border. */}
                <legend className="float-left mb-3 w-full p-0">
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "border-ink grid h-6 min-w-6 place-items-center rounded-full border-[1.5px] px-1 font-mono text-[11px] font-bold",
                        done ? "bg-acid" : "bg-paper",
                      )}
                    >
                      {done ? (
                        <Check size={12} strokeWidth={3} aria-hidden="true" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="text-ink-soft font-mono text-[11px] font-bold uppercase tracking-[0.06em]">
                      Pregunta {index + 1} de {total}
                    </span>
                  </span>
                  {/* Reading face, synthesised bold: font-semibold would
                      switch to the display face and read like a title. */}
                  <span className="person-name mt-2 block text-base leading-snug">
                    {question.prompt}
                  </span>
                </legend>
                <div className="clear-both">
                  <QuestionInput
                    question={question}
                    answer={answers[question.id]}
                    onChange={(answer) => {
                      set(question.id, answer);
                      setResult(null);
                    }}
                  />
                </div>
              </fieldset>
            </li>
          );
        })}
      </ol>

      {result &&
        (() => {
          // A written answer waits for a person: its points aren't in the
          // score yet, so this isn't a fail, it's "sent, pending review".
          const pending = result.pendingReview > 0 && !result.passed;
          return (
            <div
              role="status"
              className={cn(
                "border-ink border-surface shadow-brutal rounded-control mt-5 flex flex-wrap items-center gap-4 p-4",
                result.passed ? "bg-acid" : pending ? "bg-grape" : "bg-lemon",
              )}
            >
              <strong className="font-display text-4xl leading-none">
                {result.score}%
              </strong>
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-extrabold">
                  {result.passed
                    ? "¡Aprobaste el quiz!"
                    : pending
                      ? "Respuestas enviadas"
                      : "Todavía no apruebas"}
                </p>
                <p className="text-sm">
                  {result.passed
                    ? "Buen trabajo. Puedes seguir con la próxima clase."
                    : pending
                      ? `Tu puntaje parcial es ${result.score}%. ${result.pendingReview === 1 ? "La respuesta escrita la revisa" : "Las respuestas escritas las revisa"} el equipo y te avisamos con el resultado final.`
                      : `Necesitas ${data.quiz.passingScore}%. Repasa la clase y vuelve a intentarlo.`}
                  {result.passed &&
                    result.pendingReview > 0 &&
                    " Una parte escrita queda pendiente de revisión."}
                </p>
              </div>
              {!result.passed && !pending && (
                <button
                  type="button"
                  onClick={() => {
                    reset();
                    setResult(null);
                  }}
                  className={`${btnSecondary} px-4 py-2.5 text-sm`}
                >
                  Intentar de nuevo
                </button>
              )}
            </div>
          );
        })()}
      {error && (
        <p role="alert" className={cn(fieldErrorClass, "mt-4")}>
          {error}
        </p>
      )}
      <div
        className={cn(
          "mt-5 flex flex-wrap items-center gap-3",
          result && "hidden",
        )}
      >
        <button
          disabled={!complete || sending}
          className={`${btnPrimary} gap-2 px-5 py-3 text-sm disabled:cursor-not-allowed`}
        >
          <Check size={16} aria-hidden="true" />
          {sending ? "Revisando…" : "Revisar respuestas"}
        </button>
        {!complete && (
          <span className="text-ink-soft text-sm">
            Responde{" "}
            {total - answered === 1
              ? "la pregunta que falta"
              : `las ${total - answered} preguntas que faltan`}{" "}
            para revisar.
          </span>
        )}
      </div>
    </form>
  );
}

function ProjectPanel({ projectId }: { projectId: string }) {
  type ProjectData = {
    instructions: string;
    rubric: string;
  };
  const projectQuery = useQuery({
    queryKey: ["student", "project", projectId],
    queryFn: () =>
      apiFetch<{ project: ProjectData }>(
        `/api/education/projects/${projectId}`,
      ),
  });
  const project = projectQuery.data?.project;
  const [notice, setNotice] = useState("");
  if (projectQuery.isError)
    return (
      <p className="border-ink bg-bubble border-chip p-3">
        No pudimos cargar el proyecto.
      </p>
    );
  if (!project) return <TabPanelSkeleton rows={2} />;
  return (
    <div>
      <Markdown>{project.instructions}</Markdown>
      {project.rubric && (
        <details className="border-ink bg-bone border-chip mt-4 p-3">
          <summary className="font-bold">Ver rúbrica</summary>
          <Markdown className="mt-3 text-sm">{project.rubric}</Markdown>
        </details>
      )}
      <form
        className="mt-5 space-y-3"
        onSubmit={async (event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const data = new FormData(form);
          await apiFetch(`/api/education/projects/${projectId}/submissions`, {
            method: "POST",
            body: JSON.stringify({
              body: data.get("body"),
              url: data.get("url") || null,
              submit: true,
            }),
          });
          setNotice("Proyecto enviado. Te avisaremos cuando reciba feedback.");
          form.reset();
        }}
      >
        <textarea
          name="body"
          rows={5}
          placeholder="Cuenta qué construiste y las decisiones que tomaste."
          className={cn(inputClass, "mt-0 w-full p-3 text-sm")}
        />
        <input
          name="url"
          type="url"
          placeholder="https://github.com/… o enlace al proyecto"
          className={cn(inputClass, "mt-0 w-full p-3 text-sm")}
        />
        {notice && (
          <p className="border-ink bg-acid border-chip p-3 text-sm">{notice}</p>
        )}
        <button className="border-ink bg-ink text-paper border-chip flex items-center gap-2 px-4 py-3 font-bold">
          <Send size={16} /> Entregar proyecto
        </button>
      </form>
    </div>
  );
}

// Human "empieza en / en vivo / terminó" for a live event, from its timestamps.
// No end set → assume ~2h so an in-progress event still reads as live.
function eventTiming(startsAt: string, endsAt: string | null) {
  const start = new Date(startsAt).getTime();
  if (Number.isNaN(start)) return { label: "", live: false, ended: false };
  const end = endsAt ? new Date(endsAt).getTime() : start + 2 * 60 * 60 * 1000;
  const now = Date.now();
  if (now < start) {
    const mins = Math.round((start - now) / 60000);
    const label =
      mins < 60
        ? `Empieza en ${mins} min`
        : mins < 60 * 24
          ? `Empieza en ${Math.round(mins / 60)} h`
          : `Empieza en ${Math.round(mins / 1440)} días`;
    return { label, live: false, ended: false };
  }
  if (now <= end) return { label: "En vivo ahora", live: true, ended: false };
  return { label: "Terminó", live: false, ended: true };
}

// Event card footer: the date + a live/soon/ended (or "Cancelado") status, the
// "Entrar a la sala" link once registered (server only sends the url to them),
// and the register/cancel toggle (UNB2-137).
function EventActions({
  item,
  pending,
  onRegister,
}: {
  item: Record<string, unknown>;
  pending: boolean;
  onRegister: (registered: boolean) => void;
}) {
  const canceled = Boolean(item.canceled);
  const registered = Boolean(item.registered);
  const meetingUrl =
    typeof item.meetingUrl === "string" ? item.meetingUrl : null;
  const startsAt = typeof item.startsAt === "string" ? item.startsAt : "";
  const endsAt = typeof item.endsAt === "string" ? item.endsAt : null;
  const timing = eventTiming(startsAt, endsAt);
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
        {startsAt && (
          <span className="text-ink-soft">
            {new Date(startsAt).toLocaleString("es-AR", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </span>
        )}
        {canceled ? (
          <span className="border-ink bg-bubble border px-2 py-0.5 font-bold uppercase">
            Cancelado
          </span>
        ) : (
          timing.label && (
            <span
              className={`border-ink border px-2 py-0.5 font-bold uppercase ${timing.live ? "bg-acid" : "bg-paper"}`}
            >
              {timing.label}
            </span>
          )
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {registered && meetingUrl && !canceled && (
          <a
            href={meetingUrl}
            target="_blank"
            rel="noreferrer"
            className={`border-ink border-chip flex items-center gap-2 px-3 py-2 font-bold ${timing.live ? "bg-acid" : "bg-ink text-paper"}`}
          >
            <Video size={15} /> Entrar a la sala
          </a>
        )}
        {!canceled && !timing.ended && (
          <button
            type="button"
            disabled={pending}
            onClick={() => onRegister(!registered)}
            className={`${btnAccent} flex items-center gap-2 px-3 py-2 disabled:cursor-wait`}
          >
            <CalendarDays size={15} />
            {pending
              ? "Guardando…"
              : registered
                ? "Cancelar registro"
                : "Registrarme"}
          </button>
        )}
      </div>
      {registered && !meetingUrl && !canceled && !timing.ended && (
        <p className="text-ink-soft text-xs">
          Vas a ver el link de la sala aquí cuando esté disponible.
        </p>
      )}
    </div>
  );
}

// SPA navigation (mirrors main.tsx's `go`): pushState + popstate so the router
// re-renders. Kept local to avoid a circular import with main.tsx.
const goTo = (path: string) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

type PathDetailData = {
  path: {
    id: string;
    slug: string;
    title: string;
    description: string;
    objective: string;
    level: string;
    selected: boolean;
  };
  courses: Array<{
    courseId: string;
    title: string;
    slug: string;
    access: boolean;
    enrolled: boolean;
    totalLessons: number;
    completedLessons: number;
    completed: boolean;
  }>;
  progress: {
    completedCourses: number;
    totalCourses: number;
    nextCourseId: string | null;
  };
};

// Detail page for a learning path (/paths/:slug): its ordered courses with the
// student's progress and a "Continuar" on the next unfinished one (UNB2-136).
export function PathDetail({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: ["student", "path", slug],
    queryFn: () => apiFetch<PathDetailData>(`/api/education/paths/${slug}`),
  });
  useEffect(() => {
    document.title = query.data
      ? `${query.data.path.title} — hey404`
      : "Ruta — hey404";
  }, [query.data]);

  const back = (
    <button
      type="button"
      onClick={() => goTo("/paths")}
      className={`${btnSecondary} inline-flex items-center gap-1 px-3 py-1.5 text-sm`}
    >
      <ChevronLeft size={16} /> Rutas
    </button>
  );

  if (query.isPending) return <PanelSkeleton cards={3} />;
  if (query.isError || !query.data)
    return (
      <section>
        {back}
        <p className="border-ink bg-bubble border-chip mt-6 p-4 text-sm">
          No pudimos cargar esta ruta.
        </p>
      </section>
    );

  const { path, courses, progress } = query.data;
  const pct = progress.totalCourses
    ? Math.round((progress.completedCourses / progress.totalCourses) * 100)
    : 0;

  return (
    <section>
      {back}
      <div className="mt-5 flex items-center gap-3">
        <Map size={28} />
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
          {path.title}
        </h1>
      </div>
      {path.objective && (
        <p className="text-ink-soft mt-2 max-w-2xl">{path.objective}</p>
      )}
      <div className="border-ink bg-bone border-surface mt-6 p-4">
        <div className="flex items-center justify-between font-mono text-xs font-bold uppercase">
          <span>Progreso</span>
          <span>
            {progress.completedCourses}/{progress.totalCourses} cursos
          </span>
        </div>
        <div className="border-ink bg-paper border-chip mt-2 h-3 w-full">
          <div className="bg-acid h-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <ol className="mt-6 space-y-3">
        {courses.map((course, index) => {
          const isNext = course.courseId === progress.nextCourseId;
          const label = course.completed
            ? "Repasar"
            : isNext
              ? "Continuar"
              : "Ver";
          return (
            <li
              key={course.courseId}
              className={`border-ink border-surface flex flex-wrap items-center gap-3 p-4 ${isNext ? "bg-lemon" : "bg-bone"}`}
            >
              <span
                className={`border-ink border-chip grid h-8 w-8 shrink-0 place-items-center font-mono text-sm font-bold ${course.completed ? "bg-acid" : "bg-paper"}`}
              >
                {course.completed ? <Check size={16} /> : index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display font-extrabold">{course.title}</p>
                <p className="text-ink-soft font-mono text-xs">
                  {course.totalLessons > 0
                    ? `${course.completedLessons}/${course.totalLessons} clases`
                    : "Sin clases todavía"}
                  {!course.access && " · sin acceso"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => goTo(`/courses/${course.slug}`)}
                className="border-ink bg-ink text-paper border-chip inline-flex items-center gap-1 px-3 py-2 text-sm font-bold"
              >
                {label} <ArrowRight size={15} />
              </button>
            </li>
          );
        })}
        {courses.length === 0 && (
          <p className="text-ink-soft text-sm">
            Esta ruta todavía no tiene cursos.
          </p>
        )}
      </ol>
    </section>
  );
}

// A team member's card body: the paths the team assigned them, each with their
// own progress and the due date, linking into the path detail (UNB2-138).
function TeamAssignments({ item }: { item: Record<string, unknown> }) {
  const assignments = (
    Array.isArray(item.assignments) ? item.assignments : []
  ) as Array<{
    pathId: string;
    pathSlug: string;
    pathTitle: string;
    dueAt: string | null;
    completedCourses: number;
    totalCourses: number;
  }>;
  if (assignments.length === 0)
    return (
      <p className="text-ink-soft mt-4 text-sm">
        Tu equipo todavía no te asignó rutas.
      </p>
    );
  return (
    <div className="mt-4 space-y-2">
      <p className="font-mono text-xs font-bold uppercase">Rutas asignadas</p>
      {assignments.map((assignment) => {
        const pct = assignment.totalCourses
          ? Math.round(
              (assignment.completedCourses / assignment.totalCourses) * 100,
            )
          : 0;
        const due = assignment.dueAt ? new Date(assignment.dueAt) : null;
        const overdue =
          due != null &&
          due.getTime() < Date.now() &&
          assignment.completedCourses < assignment.totalCourses;
        return (
          <div
            key={assignment.pathId}
            className="border-ink bg-paper border-chip p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-display min-w-0 flex-1 font-bold">
                {assignment.pathTitle}
              </span>
              {due && (
                <span
                  className={`border-ink border px-2 py-0.5 font-mono text-xs font-bold uppercase ${overdue ? "bg-bubble" : "bg-lemon"}`}
                >
                  {overdue ? "Vencida" : "Vence"}{" "}
                  {due.toLocaleDateString("es-AR", {
                    day: "2-digit",
                    month: "short",
                  })}
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="border-ink bg-bone h-2 flex-1 border">
                <div className="bg-acid h-full" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono text-xs font-bold">
                {assignment.completedCourses}/{assignment.totalCourses}
              </span>
            </div>
            <button
              type="button"
              onClick={() => goTo(`/paths/${assignment.pathSlug}`)}
              className="border-ink bg-ink text-paper border-chip mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-bold"
            >
              <Map size={14} /> Ver ruta <ArrowRight size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function LearningHub({
  section,
}: {
  section: "paths" | "events" | "notifications" | "teams";
}) {
  const queryClient = useQueryClient();
  const [pendingItem, setPendingItem] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const endpoint = section === "paths" ? "paths" : section;
  const hubQuery = useQuery({
    queryKey: ["student", "hub", endpoint],
    queryFn: () =>
      apiFetch<Record<string, unknown>>(`/api/education/${endpoint}`),
  });
  const data = hubQuery.data;
  if (hubQuery.isPending) return <PanelSkeleton cards={3} />;
  if (!data)
    return (
      <div className="border-ink bg-bubble border-surface p-5">
        <strong className="font-display text-xl">
          No pudimos cargar esta sección.
        </strong>
        <p className="text-ink-soft mt-2 text-sm">
          Revisa tu conexión y vuelve a intentarlo.
        </p>
        <button
          type="button"
          onClick={() => hubQuery.refetch()}
          className={`${btnSecondary} mt-4 px-4 py-2`}
        >
          Reintentar
        </button>
      </div>
    );
  const title = {
    paths: "Rutas de aprendizaje",
    events: "Eventos y clases en vivo",
    notifications: "Notificaciones",
    teams: "Mis equipos",
  }[section];
  const Icon =
    section === "paths"
      ? Map
      : section === "events"
        ? CalendarDays
        : section === "teams"
          ? Users
          : Bell;
  const items = (data[section] ?? []) as Array<Record<string, unknown>>;
  const empty = {
    paths: {
      title: "No hay rutas publicadas",
      body: "Cuando haya un recorrido disponible, lo vas a encontrar aquí.",
    },
    events: {
      title: "No hay próximos eventos",
      body: "Estás al día. Los próximos encuentros aparecerán aquí.",
    },
    notifications: {
      title: "No tienes avisos",
      body: "Las novedades importantes de tus cursos aparecerán aquí.",
    },
    teams: {
      title: "No perteneces a ningún equipo",
      body: "Cuando te sumen a uno, vas a poder verlo desde este espacio.",
    },
  }[section];
  const runAction = async (itemId: string, action: () => Promise<unknown>) => {
    setPendingItem(itemId);
    setActionError("");
    try {
      await action();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["student", "hub", endpoint],
        }),
        queryClient.invalidateQueries({ queryKey: ["student", "navigation"] }),
      ]);
    } catch {
      setActionError("No pudimos completar la acción. Intenta nuevamente.");
    } finally {
      setPendingItem(null);
    }
  };
  return (
    <section>
      <div className="flex items-center gap-3">
        <Icon size={28} />
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
          {title}
        </h1>
      </div>
      {actionError && (
        <p
          role="alert"
          className="border-ink bg-bubble border-chip mt-5 p-3 text-sm"
        >
          {actionError}
        </p>
      )}
      <div className="mt-7 grid gap-4 lg:grid-cols-2">
        {items.map((item, index) => (
          <article
            key={String(item.id ?? index)}
            className="border-ink bg-bone border-surface p-5"
          >
            <p className="font-mono text-xs font-bold uppercase">
              {String(item.level ?? item.kind ?? item.role ?? "hey404")}
            </p>
            <h2 className="font-display mt-2 text-2xl font-extrabold">
              {String(item.title ?? item.name ?? "Actualización")}
            </h2>
            <p className="text-ink-soft mt-2 text-sm">
              {String(item.description ?? item.body ?? item.objective ?? "")}
            </p>
            {section === "paths" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => goTo(`/paths/${String(item.slug)}`)}
                  className="border-ink bg-ink text-paper border-chip flex items-center gap-2 px-3 py-2 font-bold"
                >
                  <Map size={15} /> Ver ruta
                </button>
                <button
                  disabled={pendingItem === String(item.id)}
                  onClick={() =>
                    runAction(String(item.id), () =>
                      apiFetch(`/api/education/paths/${String(item.id)}`, {
                        method: "PUT",
                        body: JSON.stringify({
                          selected: !item.selected,
                          priority: 0,
                        }),
                      }),
                    )
                  }
                  className={`${btnAccent} flex items-center gap-2 px-3 py-2 disabled:cursor-wait`}
                >
                  <Sparkles size={15} />{" "}
                  {pendingItem === String(item.id)
                    ? "Guardando…"
                    : item.selected
                      ? "Quitar de mi plan"
                      : "Agregar a mi plan"}
                </button>
              </div>
            )}
            {section === "events" && (
              <EventActions
                item={item}
                pending={pendingItem === String(item.id)}
                onRegister={(registered) =>
                  runAction(String(item.id), () =>
                    apiFetch(
                      `/api/education/events/${String(item.id)}/registration`,
                      {
                        method: "PUT",
                        body: JSON.stringify({ registered }),
                      },
                    ),
                  )
                }
              />
            )}
            {section === "teams" && <TeamAssignments item={item} />}
            {section === "notifications" && !item.readAt && (
              <button
                disabled={pendingItem === String(item.id)}
                onClick={() =>
                  runAction(String(item.id), () =>
                    apiFetch(
                      `/api/education/notifications/${String(item.id)}/read`,
                      { method: "PATCH" },
                    ),
                  )
                }
                className={`${btnSecondary} mt-4 px-3 py-2 disabled:cursor-wait`}
              >
                {pendingItem === String(item.id)
                  ? "Guardando…"
                  : "Marcar como leída"}
              </button>
            )}
          </article>
        ))}
        {!items.length && (
          <div className="border-ink bg-paper border-chip p-5 lg:col-span-2">
            <strong className="font-display text-xl">{empty.title}</strong>
            <p className="text-ink-soft mt-2 text-sm">{empty.body}</p>
          </div>
        )}
      </div>
    </section>
  );
}

export function LearningCoach() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  return (
    <section className="mx-auto max-w-3xl">
      <div className="flex items-center gap-3">
        <Sparkles size={30} />
        <h1 className="font-display text-4xl font-extrabold">
          Coach de aprendizaje
        </h1>
      </div>
      <p className="text-ink-soft mt-3">
        Pregunta sobre el contenido de tus cursos. Las respuestas se apoyan en
        clases y transcripciones.
      </p>
      <form
        className="border-ink bg-bone shadow-brutal border-surface mt-7 p-5"
        onSubmit={async (event) => {
          event.preventDefault();
          setLoading(true);
          setAnswer("");
          setError("");
          try {
            const result = await apiFetch<{ answer: string }>(
              "/api/education/assistant",
              {
                method: "POST",
                body: JSON.stringify({ question }),
              },
            );
            setAnswer(result.answer);
          } catch (cause) {
            // Surface failures instead of going silent — the per-user rate limit
            // (UNB2-80) returns 429, which apiFetch throws as "rate_limited".
            setError(
              cause instanceof Error && cause.message === "rate_limited"
                ? "Vas muy rápido para el coach. Espera un minuto y vuelve a preguntar."
                : "No pudimos responder ahora mismo. Prueba de nuevo en un momento.",
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        <textarea
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          required
          minLength={3}
          rows={5}
          aria-label="Tu pregunta para el coach"
          placeholder="¿Cómo debería modelar las relaciones entre estas tablas?"
          className={cn(inputClass, "mt-0 w-full p-4")}
        />
        <button
          disabled={loading}
          className={`${btnAccent} mt-3 flex items-center gap-2 px-4 py-3`}
        >
          <Sparkles size={16} />{" "}
          {loading ? "Buscando en tus clases…" : "Preguntar"}
        </button>
      </form>
      {error && (
        <p
          role="alert"
          aria-live="assertive"
          className="border-ink bg-bubble border-surface mt-6 p-4 text-sm font-bold"
        >
          {error}
        </p>
      )}
      {answer && (
        <article className="border-ink bg-paper border-surface mt-6 p-5">
          <Markdown>{answer}</Markdown>
        </article>
      )}
    </section>
  );
}
